import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  TextField, 
  Button, 
  Divider, 
  Avatar,
  Alert,
  CircularProgress
} from '@mui/material';
import AppLayout from '../components/layout/AppLayout';
import { useAuth } from '../contexts/AuthContext';
import { updateUserProfile } from '../services/authService';

const ProfilePage: React.FC = () => {
  const { userProfile, refreshUserProfile } = useAuth();
  const [nickname, setNickname] = useState(userProfile?.nickname || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleUpdateProfile = async () => {
    if (!nickname.trim()) {
      setError('Nickname cannot be empty');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      await updateUserProfile({ nickname });
      await refreshUserProfile();
      
      setSuccess('Profile updated successfully!');
    } catch (err: any) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppLayout>
      <Box sx={{ maxWidth: 800, mx: 'auto' }}>
        <Typography variant="h4" gutterBottom>
          My Profile
        </Typography>
        <Divider sx={{ mb: 3 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Avatar 
              sx={{ width: 80, height: 80, mr: 3 }} 
              alt={userProfile?.nickname || 'User'}
            >
              {userProfile?.nickname?.[0] || 'U'}
            </Avatar>
            <Box>
              <Typography variant="h5">{userProfile?.nickname || 'User'}</Typography>
              <Typography variant="body2" color="text.secondary">
                {userProfile?.email || ''}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Member since {userProfile?.createdAt ? new Date(userProfile.createdAt).toLocaleDateString() : 'N/A'}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Typography variant="h6" gutterBottom>
            Edit Profile
          </Typography>

          <TextField
            fullWidth
            label="Nickname"
            variant="outlined"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            sx={{ mb: 3 }}
          />

          <Button 
            variant="contained" 
            onClick={handleUpdateProfile}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Update Profile'}
          </Button>
        </Paper>

        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            mt: 3,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6" gutterBottom>
            Account Statistics
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="body1">Coin Balance:</Typography>
            <Typography variant="body1" fontWeight="bold">
              {userProfile?.coinBalance || 0} coins
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="body1">Account Type:</Typography>
            <Typography variant="body1" fontWeight="bold">
              {userProfile?.isAdmin ? 'Administrator' : 'Regular User'}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body1">Last Active:</Typography>
            <Typography variant="body1" fontWeight="bold">
              {userProfile?.lastActive ? new Date(userProfile.lastActive).toLocaleString() : 'N/A'}
            </Typography>
          </Box>
        </Paper>
      </Box>
    </AppLayout>
  );
};

export default ProfilePage;
